import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin, Youtube } from 'lucide-react';
import { useBrandName, useBrandShort, useSettings } from '../context/SettingsContext';
import { DEFAULT_BRAND, mediaSrc } from '../config/brand';
import EditableText from './EditableText';
const Footer = () => {
  const brandName = useBrandName();
  const brandShort = useBrandShort();
  const { settings } = useSettings();

  const contactEmail = settings.contact_email || DEFAULT_BRAND.contact_email;
  const contactPhone = settings.contact_phone || DEFAULT_BRAND.contact_phone;
  const contactAddress = settings.contact_address || DEFAULT_BRAND.contact_address;
  const logoIconUrl = settings.logo_url || DEFAULT_BRAND.logo_url;
  const logoLongUrl =
    settings.logo_long_url || DEFAULT_BRAND.logo_long_url || logoIconUrl;

  const socialLinks = [
    { key: 'social_facebook', icon: Facebook, label: 'Facebook' },
    { key: 'social_instagram', icon: Instagram, label: 'Instagram' },
    { key: 'social_linkedin', icon: Linkedin, label: 'LinkedIn' },
    { key: 'social_youtube', icon: Youtube, label: 'YouTube' },
  ].filter(({ key }) => settings[key]);

  const linkClass =
    'text-slate-600 hover:text-[#c47a0a] text-sm transition-colors inline-block';

  return (
    <footer className="bg-slate-50 border-t border-slate-200 relative">
      <div
        className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#3a00e0] via-[#18449e] to-[#fab03a]"
        aria-hidden="true"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* About */}
          <div className="space-y-4">
            <div className="space-y-2.5">
              {logoLongUrl || logoIconUrl ? (
                <img
                  src={mediaSrc(logoLongUrl)}
                  alt={brandName}
                  className="h-14 sm:h-16 w-auto max-w-[280px] object-contain"
                />
              ) : (
                <div className="flex items-center gap-2.5">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#3a00e0] to-[#18449e] rounded-xl flex items-center justify-center">
                    <span className="text-white font-bold text-lg">{brandShort}</span>
                  </div>
                  <span className="text-slate-900 font-bold text-lg tracking-wide">
                    {brandName.toUpperCase()}
                  </span>
                </div>
              )}
              <EditableText
                editKey="footer.tagline"
                defaultValue="Robotique & programmation pour enfants"
                as="p"
                className="text-[#3a00e0] text-xs font-semibold uppercase tracking-widest"
              />
            </div>
            <EditableText
              editKey="footer.description"
              defaultValue="Ateliers de robotique et programmation pour enfants et ados. Cultivons ensemble les compétences du futur."
              as="p"
              multiline
              className="text-slate-600 text-sm leading-relaxed"
            />
            {socialLinks.length > 0 && (
              <div className="flex gap-3">
                {socialLinks.map(({ key, icon: Icon, label }) => (
                  <a
                    key={key}
                    href={settings[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-[#c47a0a] hover:border-[rgba(250,176,58,0.5)] hover:bg-[rgba(250,176,58,0.08)] transition-colors"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Nos Offres */}
          <div>
            <EditableText
              editKey="footer.offers.title"
              defaultValue="Nos Offres"
              as="h3"
              className="text-slate-900 font-semibold mb-1 text-sm uppercase tracking-wider"
            />
            <span className="section-title-rule mb-4" aria-hidden="true" />
            <ul className="space-y-2.5">
              {[
                { to: '/ateliers', label: 'Ateliers Découverte', editKey: 'footer.offers.ateliers' },
                { to: '/cursus', label: 'Cursus Annuel', editKey: 'footer.offers.cursus' },
                { to: '/stages', label: 'Stages Vacances', editKey: 'footer.offers.stages' },
                { to: '/boutique', label: 'Boutique Robots', editKey: 'footer.offers.boutique' },
                { to: '/elearning', label: 'Plateforme E-learning', editKey: 'footer.offers.elearning' },
                { to: '/entreprises', label: 'Offres Entreprises', editKey: 'footer.offers.entreprises' },
              ].map(({ to, label, editKey }) => (
                <li key={to}>
                  <Link to={to} className={linkClass}>
                    <EditableText editKey={editKey} defaultValue={label} as="span" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Informations */}
          <div>
            <EditableText
              editKey="footer.info.title"
              defaultValue="Informations"
              as="h3"
              className="text-slate-900 font-semibold mb-1 text-sm uppercase tracking-wider"
            />
            <span className="section-title-rule mb-4" aria-hidden="true" />
            <ul className="space-y-2.5">
              {[
                { to: '/about', label: 'À Propos', editKey: 'footer.info.about' },
                { to: '/blog', label: 'Blog', editKey: 'footer.info.blog' },
                { to: '/contact', label: 'Contact', editKey: 'footer.info.contact' },
                { to: '/mentions-legales', label: 'Mentions Légales', editKey: 'footer.info.mentions' },
                { to: '/politique-confidentialite', label: 'Politique de Confidentialité', editKey: 'footer.info.politique' },
                { to: '/rgpd', label: 'RGPD', editKey: 'footer.info.rgpd' },
                { to: '/cgv', label: 'CGV', editKey: 'footer.info.cgv' },
              ].map(({ to, label, editKey }) => (
                <li key={to}>
                  <Link to={to} className={linkClass}>
                    <EditableText editKey={editKey} defaultValue={label} as="span" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <EditableText
              editKey="footer.contact.title"
              defaultValue="Contact"
              as="h3"
              className="text-slate-900 font-semibold mb-1 text-sm uppercase tracking-wider"
            />
            <span className="section-title-rule mb-4" aria-hidden="true" />
            <ul className="space-y-4">
              <li className="flex items-start gap-3 group">
                <div className="w-9 h-9 rounded-lg bg-[rgba(58,0,224,0.08)] border border-[rgba(58,0,224,0.15)] flex items-center justify-center shrink-0">
                  <MapPin className="h-4 w-4 text-[#3a00e0]" />
                </div>
                <span className="text-slate-600 text-sm leading-relaxed pt-1.5">{contactAddress}</span>
              </li>
              <li className="flex items-center gap-3 group">
                <div className="w-9 h-9 rounded-lg bg-[rgba(250,176,58,0.16)] border border-[rgba(250,176,58,0.4)] flex items-center justify-center shrink-0">
                  <Phone className="h-4 w-4 text-[#c47a0a]" />
                </div>
                <a
                  href={`tel:${contactPhone.replace(/\s/g, '')}`}
                  className="text-slate-600 text-sm hover:text-[#c47a0a] transition-colors"
                >
                  {contactPhone}
                </a>
              </li>
              <li className="flex items-center gap-3 group">
                <div className="w-9 h-9 rounded-lg bg-[rgba(58,0,224,0.08)] border border-[rgba(58,0,224,0.15)] flex items-center justify-center shrink-0">
                  <Mail className="h-4 w-4 text-[#3a00e0]" />
                </div>
                <a
                  href={`mailto:${contactEmail}`}
                  className="text-slate-600 text-sm hover:text-[#c47a0a] transition-colors"
                >
                  {contactEmail}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-8 text-center">
          <EditableText
            editKey="footer.copyright"
            defaultValue="Tous droits réservés. Partenaire pédagogique Algora."
            as="p"
            className="text-slate-500 text-sm"
            formatDisplay={(text) => `© ${new Date().getFullYear()} ${brandName.toUpperCase()}. ${text}`}
          />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
